import React, { useState, useEffect } from 'react';
import EditorTile from '../EditorTile';
import { ExportIcon, ImportIcon, TopIcon, EyeIcon, EyeClosedIcon, DeleteIcon, HomeIcon } from '../icons/Icons';
import { Link } from 'react-router-dom';
import { exportToZip, importFromZip } from '../utils/exportImport';
import { handleDragStart, handleDragOver, handleDrop } from '../utils/dragDrop';
import '../textEditor.css';

const TextEditor = () => {
    const [editors, setEditors] = useState([
        { id: 1, heading: 'heading 1', content: '', isVisible: true, isActive: true }
    ]);
    const [activeEditorId, setActiveEditorId] = useState(1);
    const [showTopButton, setShowTopButton] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setShowTopButton(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const addNewEditor = () => {
        const newId = Date.now();
        const newEditor = {
            id: newId,
            heading: `heading ${editors.length + 1}`,
            content: '',
            isVisible: true,
            isActive: false
        };
        setEditors([...editors, newEditor]);
        setActiveEditorId(newId);
    };

    const updateEditor = (id, updates) => {
        setEditors(editors.map(editor => 
            editor.id === id ? { ...editor, ...updates } : editor
        ));
    };

    const deleteEditor = (id) => {
        if (editors.length === 1) return;
        const newEditors = editors.filter(editor => editor.id !== id);
        if (activeEditorId === id && newEditors.length > 0) {
            setActiveEditorId(newEditors[0].id);
        }
        setEditors(newEditors);
    };

    const toggleVisibility = (id) => {
        setEditors(editors.map(editor =>
            editor.id === id ? { ...editor, isVisible: !editor.isVisible } : editor
        ));
    };

    const handleExport = async () => {
        await exportToZip(editors);
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (file && file.name.endsWith('.zip')) {
            try {
                const newEditors = await importFromZip(file);
                if (newEditors && newEditors.length > 0) {
                    // Stelle sicher, dass neue IDs eindeutig sind
                    const editorsWithUniqueIds = newEditors.map(editor => ({
                        ...editor,
                        id: Date.now() + Math.random() * 10000 + editor.id
                    }));
                    setEditors(prev => [...prev, ...editorsWithUniqueIds]);
                }
            } catch (error) {
                console.error('Import failed:', error);
                alert('Import failed. Please make sure you selected a valid ZIP file.');
            }
        } else {
            alert('Please select a valid ZIP file.');
        }
        event.target.value = ''; // Reset input
    };

    return (
        <div className="text-editor-container">
            <div className="global-actions">
                <Link to="/" className="global-action-btn home-btn">
                    <HomeIcon /> home
                </Link>
                <button onClick={handleExport} className="global-action-btn">
                    <ExportIcon /> export
                </button>
                <button onClick={() => document.getElementById('zip-import').click()} className="global-action-btn">
                    <ImportIcon /> import
                </button>
                <input
                    id="zip-import"
                    type="file"
                    accept=".zip"
                    onChange={handleImport}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Tabs Navigation */}
            <div className="editor-tabs">
                {editors.map((editor, index) => (
                    <div
                        key={editor.id}
                        className={`editor-tab ${activeEditorId === editor.id ? 'active' : ''} ${!editor.isVisible ? 'muted' : ''}`}
                        onClick={() => setActiveEditorId(editor.id)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index, editors, setEditors)}
                    >
                        <span className="tab-heading">{editor.heading}</span>
                        <div className="tab-actions">
                            <button 
                                className="tab-btn"
                                onClick={(e) => { e.stopPropagation(); toggleVisibility(editor.id); }}
                                title={editor.isVisible ? "hide" : "show"}
                            >
                                {editor.isVisible ? <EyeIcon /> : <EyeClosedIcon />}
                            </button>
                            <button 
                                className="tab-btn"
                                onClick={(e) => { e.stopPropagation(); deleteEditor(editor.id); }}
                                title="delete"
                            >
                                <DeleteIcon />
                            </button>
                        </div>
                    </div>
                ))}
                <button onClick={addNewEditor} className="add-tab-btn" title="add new">+</button>
            </div>

            {/* Editor Tiles */}
            <div className="editors-container">
                {editors.map(editor => (
                    <EditorTile
                        key={editor.id}
                        editor={editor}
                        isActive={activeEditorId === editor.id}
                        onUpdate={(updates) => updateEditor(editor.id, updates)}
                        onActivate={() => setActiveEditorId(editor.id)}
                    />
                ))}
            </div>

            {/* Scroll to Top Button */}
            {showTopButton && (
                <button onClick={scrollToTop} className="scroll-top-btn" title="to top">
                    <TopIcon />
                </button>
            )}
        </div>
    );
};

export default TextEditor;